import { Box, Collapse, Divider, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Fragment, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { NavigationItem } from '@core/router/types';
import { useAuth } from '@core/auth/hooks/useAuth';
import { getModuleNavigation } from '@core/router/moduleRegistry';
import { useAccessibleModules } from '@core/router/useAccessibleModules';
import { CurrentModuleHeader } from './CurrentModuleHeader';

interface SidebarProps {
  onItemClick?: () => void; // For closing mobile drawer
}

const isAllowed = (can: (permission: string) => boolean, item: NavigationItem) => {
  if (!item.permission) return true;
  return can(item.permission);
};

const filterNav = (can: (permission: string) => boolean, items: NavigationItem[]): NavigationItem[] =>
  items
    .filter((i) => isAllowed(can, i))
    .map((i) => ({
      ...i,
      children: i.children ? filterNav(can, i.children) : undefined,
    }))
    .filter((i) => !i.children || i.children.length > 0 || !!i.path);

/**
 * Sidebar Navigation
 *
 * Module-aware sidebar with:
 * - Current module name at the top, which also opens the module switcher
 * - Dashboard link (always visible)
 * - Module-specific navigation based on current route
 * - Permission-based filtering
 * - Active state highlighting
 * - Mobile drawer close on item click
 */
export const Sidebar = ({ onItemClick }: SidebarProps) => {
  const { can } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  // Current module from the URL; on dashboard or other non-module pages
  // the first accessible module's navigation is shown
  const { currentModule } = useAccessibleModules();
  const moduleName = currentModule?.id;

  // Get navigation items for the current module
  const moduleNavItems = useMemo(() => {
    if (!moduleName) return [];
    return getModuleNavigation(moduleName);
  }, [moduleName]);

  // Filter navigation based on permissions
  const visibleItems = useMemo(() => filterNav(can, moduleNavItems), [can, moduleNavItems]);

  // Collect all navigable paths from visible items for smart matching
  const allPaths = useMemo(() => {
    const paths: string[] = [];
    const collectPaths = (items: NavigationItem[]) => {
      items.forEach((item) => {
        if (item.path) paths.push(item.path);
        if (item.children) collectPaths(item.children);
      });
    };
    collectPaths(visibleItems);
    return paths;
  }, [visibleItems]);

  // Smart path matching: match child routes to parent nav item,
  // but not when a more specific nav item exists
  const isPathActive = (itemPath: string): boolean => {
    const currentPath = location.pathname;

    // Exact match - always active
    if (currentPath === itemPath) return true;

    // Check if current path is a child of item path (e.g., /vozila/123 is child of /vozila)
    if (currentPath.startsWith(itemPath + '/')) {
      // Check if any OTHER nav item is a better (more specific) match
      const hasMoreSpecificMatch = allPaths.some((path) =>
        path !== itemPath &&
        path.startsWith(itemPath + '/') &&
        (currentPath === path || currentPath.startsWith(path + '/'))
      );
      return !hasMoreSpecificMatch;
    }

    return false;
  };

  const renderNavItem = (item: NavigationItem, depth = 0) => {
    const key = `${item.label}:${item.path ?? 'group'}`;
    const hasChildren = !!item.children?.length;

    // Check if any child is active (for parent highlighting)
    const hasActiveChild = hasChildren && item.children?.some((child) =>
      child.path && isPathActive(child.path)
    );

    const groupOpen = openGroups[key] ?? hasActiveChild;

    // Render leaf item (no children) - navigable
    if (!hasChildren && item.path) {
      const isActive = isPathActive(item.path);

      return (
        <Fragment key={key}>
          {/* Add divider if specified */}
          {item.dividerBefore && <Divider sx={{ my: 1 }} />}

          <ListItemButton
            component={NavLink}
            to={item.path}
            onClick={onItemClick}
            className={isActive ? 'active' : undefined}
            // Use 'end' prop to prevent NavLink from matching parent paths
            // e.g., /vozni-park/vozila should NOT be active when on /vozni-park/vozila/zahtevi
            end
            sx={{
              pl: 2 + depth * 2,
              py: depth === 0 ? 1.25 : 0.875,
              borderRadius: 0,
              '&.active': {
                bgcolor: 'action.selected',
                borderRight: '2px solid',
                borderColor: 'primary.main',
                '& .MuiListItemText-primary': {
                  fontWeight: 600,
                  color: 'primary.main',
                },
                '& .MuiListItemIcon-root': {
                  color: 'primary.main',
                },
              },
              '&:hover': {
                bgcolor: 'action.hover',
              },
              transition: 'all 0.15s ease-in-out',
            }}
          >
            {item.icon && (
              <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                {item.icon}
              </ListItemIcon>
            )}
            <ListItemText
              primary={item.labelKey ? t(item.labelKey) : item.label}
              slotProps={{
                primary: {
                  sx: {
                    fontSize: depth === 0 ? '0.875rem' : '0.8125rem',
                    fontWeight: 400,
                  },
                },
              }}
            />
          </ListItemButton>
        </Fragment>
      );
    }

    // Render expandable group (has children)
    return (
      <Fragment key={key}>
        <ListItemButton
          onClick={() => setOpenGroups((s) => ({ ...s, [key]: !groupOpen }))}
          sx={{
            pl: 2 + depth * 2,
            py: 1.25,
            '&:hover': {
              bgcolor: 'action.hover',
            },
            transition: 'all 0.15s ease-in-out',
          }}
        >
          {item.icon && (
            <ListItemIcon sx={{
              minWidth: 36,
              color: hasActiveChild ? 'primary.main' : 'text.secondary',
              transition: 'color 0.15s ease-in-out',
            }}>
              {item.icon}
            </ListItemIcon>
          )}
          <ListItemText
            primary={item.labelKey ? t(item.labelKey) : item.label}
            slotProps={{
              primary: {
                sx: {
                  fontSize: '0.875rem',
                  fontWeight: hasActiveChild ? 600 : 500,
                  color: hasActiveChild ? 'primary.main' : 'text.primary',
                },
              },
            }}
          />
          {groupOpen ? (
            <ExpandLess sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
          ) : (
            <ExpandMore sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
          )}
        </ListItemButton>
        <Collapse in={groupOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            {item.children?.map((child) => renderNavItem(child, depth + 1))}
          </List>
        </Collapse>
      </Fragment>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
      {/* Current module name at the top — fixed, does not scroll */}
      <CurrentModuleHeader onModuleChange={onItemClick} />

      {/* Navigation list */}
      <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
        <List disablePadding>
          {/* Dashboard - Always visible */}
          <ListItemButton
            component={NavLink}
            to="/dashboard"
            onClick={onItemClick}
            sx={{
              py: 1,
              '&.active': {
                bgcolor: 'action.selected',
                borderRight: '3px solid',
                borderColor: 'primary.main',
                '& .MuiListItemText-primary': {
                  fontWeight: 600,
                  color: 'primary.main',
                },
                '& .MuiListItemIcon-root': {
                  color: 'primary.main',
                },
              },
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('nav.dashboard')}
              slotProps={{
                primary: {
                  sx: {
                    fontSize: '0.875rem',
                  },
                },
              }}
            />
          </ListItemButton>

          {/* Module-specific navigation */}
          {visibleItems.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              {visibleItems.map((item) => renderNavItem(item))}
            </>
          )}
        </List>
      </Box>
    </Box>
  );
};
